// Iter 598 — Tinjau & Unduh mockup Shopee di Pantau Produksi (Rido/Gudang/Owner). Baca saja.
import { useCallback, useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function errText(res) {
  if (res.status === 503) return "Penyimpanan sedang sibuk, coba lagi";
  let d = "";
  try { d = (await res.json())?.detail || ""; } catch {}
  if (res.status === 404) return d || "File tidak ditemukan";
  if (res.status === 403) return d || "Anda tidak berwenang membuka mockup ini";
  return d || `Gagal (HTTP ${res.status})`;
}

export function ShopeeMockupModal({ jobId, orderNumber, customerName, onClose }) {
  const [meta, setMeta] = useState(null);
  const [metaErr, setMetaErr] = useState("");
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [nonce, setNonce] = useState(0);
  const [img, setImg] = useState({ loading: true, err: "", url: "", isPdf: false });
  const [dl, setDl] = useState({ busy: false, err: "" });
  const items = meta?.items || [];
  const cur = items[idx];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (items.length > 1 && e.key === "ArrowRight") setIdx(i => (i + 1) % items.length);
      if (items.length > 1 && e.key === "ArrowLeft") setIdx(i => (i - 1 + items.length) % items.length);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, items.length]);

  useEffect(() => {
    let alive = true;
    setMetaErr("");
    fetch(`${API}/shopee/${encodeURIComponent(jobId)}/monitor-mockups`, { credentials: "include", cache: "no-store" })
      .then(async r => { if (!alive) return; if (r.ok) setMeta(await r.json()); else setMetaErr(await errText(r)); })
      .catch(e => alive && setMetaErr("Gagal memuat daftar mockup: " + (e?.message || e)));
    return () => { alive = false; };
  }, [jobId, nonce]);

  useEffect(() => {
    if (!cur) return undefined;
    let alive = true, made = "";
    setImg({ loading: true, err: "", url: "", isPdf: false });
    setDl({ busy: false, err: "" });
    setZoom(1);
    fetch(`${API}/shopee/files/${encodeURIComponent(cur.file_id)}/preview?_=${nonce}`, { credentials: "include", cache: "no-store" })
      .then(async r => {
        if (!alive) return;
        if (!r.ok) { setImg({ loading: false, err: await errText(r), url: "", isPdf: false }); return; }
        const ct = r.headers.get("content-type") || "";
        made = URL.createObjectURL(await r.blob());
        if (!alive) { URL.revokeObjectURL(made); return; }
        setImg({ loading: false, err: "", url: made, isPdf: ct.includes("pdf") });
      })
      .catch(e => alive && setImg({ loading: false, err: "Gagal memuat mockup: " + (e?.message || e), url: "", isPdf: false }));
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [cur?.file_id, nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = useCallback(async () => {
    if (!cur) return;
    setDl({ busy: true, err: "" });
    try {
      const r = await fetch(`${API}/shopee/files/${encodeURIComponent(cur.file_id)}/download`, { credentials: "include", cache: "no-store" });
      if (!r.ok) { setDl({ busy: false, err: await errText(r) }); return; }
      const m = (r.headers.get("content-disposition") || "").match(/filename="?([^";]+)"?/i);
      const u = URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = m ? m[1] : `${orderNumber}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 4000);
      setDl({ busy: false, err: "" });
    } catch (e) { setDl({ busy: false, err: "Unduhan gagal: " + (e?.message || e) }); }
  }, [cur, orderNumber]);

  const retry = () => setNonce(n => n + 1);
  return <div className="mockup-modal-backdrop" data-testid="shopee-mockup-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="mockup-modal" role="dialog" aria-label={`Mockup ${orderNumber}`}>
      <div className="mockup-modal-head">
        <div>
          <p className="eyebrow">MOCKUP SHOPEE</p>
          <h3>{customerName || "—"}</h3>
          <p className="muted small" data-testid="shopee-mockup-meta">{orderNumber}
            {cur && <> · {cur.title} · Versi {cur.version} · {cur.status}</>}
            {items.length > 1 && <> · Mockup {idx + 1} dari {items.length}</>}</p>
        </div>
        <div className="mockup-modal-actions">
          {items.length > 1 && <>
            <button type="button" className="soft-btn small-btn" data-testid="shopee-mockup-prev" onClick={() => setIdx((idx - 1 + items.length) % items.length)}>‹ Sebelumnya</button>
            <button type="button" className="soft-btn small-btn" data-testid="shopee-mockup-next" onClick={() => setIdx((idx + 1) % items.length)}>Berikutnya ›</button>
          </>}
          {img.url && !img.isPdf && <>
            <button type="button" className="soft-btn small-btn" aria-label="Perkecil" data-testid="shopee-mockup-zoom-out" disabled={zoom <= 1} onClick={() => setZoom(z => Math.max(1, z - 0.5))}>−</button>
            <span className="muted small" data-testid="shopee-mockup-zoom-level">{Math.round(zoom * 100)}%</span>
            <button type="button" className="soft-btn small-btn" aria-label="Perbesar" data-testid="shopee-mockup-zoom-in" disabled={zoom >= 4} onClick={() => setZoom(z => Math.min(4, z + 0.5))}>+</button>
          </>}
          {cur && <button type="button" className="soft-btn small-btn" data-testid="shopee-mockup-download"
            disabled={dl.busy || !meta?.can_download} title={meta?.can_download ? "Unduh versi cetak (dengan barcode)" : meta?.download_block_reason}
            onClick={download}><Download size={14} /> {dl.busy ? "Mengunduh…" : "Unduh Mockup"}</button>}
          <button className="icon-btn" onClick={onClose} data-testid="shopee-mockup-close" aria-label="Tutup"><X size={20} /></button>
        </div>
      </div>
      {cur && !meta?.can_download && meta?.download_block_reason && <div className="muted small" style={{ padding: "0 16px 6px" }} data-testid="shopee-mockup-download-reason">{meta.download_block_reason}</div>}
      {dl.err && <div className="mockup-status mockup-error" role="alert" style={{ margin: "0 16px 6px", padding: 8 }} data-testid="shopee-mockup-download-error">{dl.err}</div>}
      {items.length > 1 && <div style={{ display: "flex", gap: 6, padding: "0 16px 8px", overflowX: "auto" }} data-testid="shopee-mockup-strip">
        {items.map((it, i) => <button key={it.file_id} type="button" onClick={() => setIdx(i)} data-testid={`shopee-mockup-strip-${i}`}
          style={{ border: i === idx ? "2px solid #0f766e" : "1px solid #cbd5e1", borderRadius: 6, padding: 2, background: "#fff", cursor: "pointer" }}
          title={`${it.title} · Versi ${it.version}`}>
          <img src={`${API}/shopee/files/${encodeURIComponent(it.file_id)}/thumbnail?size=128`} alt={it.title} style={{ width: 56, height: 56, objectFit: "contain", display: "block" }} />
        </button>)}
      </div>}
      <div className="mockup-modal-body">
        {!meta && !metaErr && <div className="mockup-status" data-testid="shopee-mockup-loading">Memuat mockup…</div>}
        {metaErr && <div className="mockup-status mockup-error" data-testid="shopee-mockup-error"><p style={{ margin: "0 0 10px" }}>{metaErr}</p>
          <button type="button" className="soft-btn small-btn" onClick={retry} data-testid="shopee-mockup-retry">Coba Lagi</button></div>}
        {meta && !items.length && <div className="mockup-status" data-testid="shopee-mockup-empty">{meta.empty_reason || "Mockup belum tersedia"}</div>}
        {cur && img.loading && <div className="mockup-status" data-testid="shopee-mockup-image-loading">Memuat mockup…</div>}
        {cur && img.err && <div className="mockup-status mockup-error" data-testid="shopee-mockup-image-error"><p style={{ margin: "0 0 10px" }}>{img.err}</p>
          <button type="button" className="soft-btn small-btn" onClick={retry} data-testid="shopee-mockup-image-retry">Coba Lagi</button></div>}
        {cur && img.url && (img.isPdf
          ? <iframe title="Mockup PDF" src={`${img.url}#toolbar=0&navpanes=0&view=FitH`} className="mockup-preview-iframe" data-testid="shopee-mockup-iframe" />
          : <div className="mockup-zoom-wrap" style={{ overflow: zoom > 1 ? "auto" : "hidden", justifyContent: zoom > 1 ? "flex-start" : "center" }}>
              <img src={img.url} alt={`Mockup ${orderNumber}`} className="mockup-preview-img" data-testid="shopee-mockup-image"
                onClick={() => setZoom(z => (z >= 4 ? 1 : z + 0.5))}
                style={zoom > 1 ? { maxWidth: "none", maxHeight: "none", width: `${zoom * 100}%`, cursor: "zoom-in" } : { cursor: "zoom-in" }} />
            </div>)}
      </div>
    </div>
  </div>;
}

export default ShopeeMockupModal;
